// Join screen: pick a name, see who is already on the server, press PLAY.
// Shown once the world has loaded; the game runs behind it. Join/leave
// updates keep the list live while the player hesitates over the name field.
import { injectTheme } from './theme';
import { setKeyboardModal } from '../player/input';

export class JoinScreen {
  constructor(net, onPlay) {
    this.net = net;
    this.onPlay = onPlay;
    injectTheme();
    const css = document.createElement('style');
    css.textContent = `
      #join { position:fixed; inset:0; z-index:30; display:flex; align-items:center;
              justify-content:center; background:rgba(0,0,0,.55);
              backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
              font:400 var(--ui-s)/1.4 var(--ui-font); letter-spacing:var(--ui-track); }
      #join .card { width:400px; background:rgba(12,12,12,.86); border:1px solid var(--hud-line);
                    border-radius:var(--hud-radius); padding:26px 30px; color:var(--hud-text); }
      #join h1 { margin:0 0 2px; font-size:26px; font-weight:600; letter-spacing:.3em; color:var(--hud-text); }
      #join .sub { color:var(--hud-dim); font-size:var(--ui-xs); margin-bottom:18px; }
      #join label { font-size:var(--ui-xs); font-weight:600; color:var(--hud-dim);
                    text-transform:uppercase; letter-spacing:.12em; }
      #join input { width:100%; box-sizing:border-box; margin:4px 0 14px; padding:9px 10px;
                    background:rgba(0,0,0,.5); color:var(--hud-text); border:1px solid var(--hud-line);
                    border-radius:var(--hud-radius); font:400 var(--ui-m) var(--ui-font); outline:none; }
      #join input:focus { border-color:rgba(255,255,255,.3); }
      #join .status { font-size:var(--ui-xs); margin-bottom:6px; }
      #join .status .dot { display:inline-block; width:8px; height:8px; border-radius:50%;
                           margin-right:6px; background:#888; }
      #join .status.online .dot { background:#7a9c5c; }
      #join .status.offline .dot { background:#c0392b; }
      #join ul { list-style:none; margin:4px 0 16px; padding:0; max-height:150px; overflow-y:auto; }
      #join li { padding:3px 0 3px 4px; color:var(--hud-text); font-size:var(--ui-s); }
      #join li::before { content:'▸ '; color:var(--hud-dim); }
      #join .empty { color:var(--hud-dim); font-style:italic; font-size:var(--ui-s); padding:3px 0 3px 4px; }
      #join button { width:100%; padding:11px; background:rgba(255,255,255,.12);
                     border:1px solid rgba(255,255,255,.25); color:var(--hud-text);
                     border-radius:var(--hud-radius); font:600 var(--ui-m) var(--ui-font);
                     letter-spacing:.2em; cursor:pointer; }
      #join button:hover { background:rgba(255,255,255,.2); }
      #join .hint { color:var(--hud-dim); font-size:var(--ui-xs); margin-top:10px; }
    `;
    document.head.appendChild(css);
    this.root = document.createElement('div');
    this.root.id = 'join';
    this.root.innerHTML = `
      <div class="card">
        <h1>ASHVALE</h1>
        <div class="sub">Shared world — survive together</div>
        <label>Your name</label>
        <input id="join-name" maxlength="24" spellcheck="false">
        <div class="status"><span class="dot"></span><span id="join-status">Connecting to the world…</span></div>
        <label>Survivors online</label>
        <ul id="join-list"></ul>
        <button id="join-play">PLAY</button>
        <div class="hint">WASD move · F1 controls in game · progress is saved on the server</div>
      </div>`;
    document.body.appendChild(this.root);
    this.nameEl = this.root.querySelector('#join-name');
    this.statusEl = this.root.querySelector('#join-status');
    this.listEl = this.root.querySelector('#join-list');
    this.nameEl.value = net.name;
    this.nameEl.addEventListener('change', () => net.setName(this.nameEl.value));
    this.playEl = this.root.querySelector('#join-play');
    this.playEl.addEventListener('click', () => this.play());
    this.nameEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.play(); });
    // The dialog owns the keyboard while it is up. Without this every keystroke
    // typed into the name field ALSO reached the game: WASD walked the
    // character around behind the dialog, K killed him, R reloaded, C/X changed
    // stance, V rolled, 1-7 swapped weapons, Tab opened the inventory over the
    // top of this card — and Space could not be typed at all, because the jump
    // bind preventDefault()s it. The modal flag covers the whole lifetime of
    // the card, including the seconds before the player clicks the field.
    setKeyboardModal(true);
    this.modal = true;
    this.played = false;
    // ...and put the caret where the player is about to type, so the first
    // screen of the game does not need a click before it accepts a letter.
    this.nameEl.focus();
    const n = this.nameEl.value.length;
    this.nameEl.setSelectionRange?.(n, n); // caret at the end, nothing selected
    this.refresh();
  }

  play() {
    if (this.played) return; // Enter and a PLAY click can both arrive
    this.played = true;
    this.net.setName(this.nameEl.value);
    // Drop focus BEFORE the card leaves the DOM: a focused, removed input can
    // stay document.activeElement in some browsers, and that would keep the
    // focus gate closed over a game nobody can control any more.
    this.nameEl.blur();
    this.playEl?.blur();
    this.releaseKeyboard();
    this.root.remove();
    this.onPlay();
  }

  // Hand the keyboard back to the game exactly once, however the card goes
  // away (PLAY, Enter, or a caller that tears the screen down itself).
  releaseKeyboard() {
    if (!this.modal) return;
    this.modal = false;
    setKeyboardModal(false);
  }

  setStatus(state) {
    const box = this.root.querySelector('.status');
    box.className = 'status ' + state;
    this.statusEl.textContent = state === 'online'
      ? 'Connected — the world is shared'
      : state === 'connecting' ? 'Connecting to the world…'
        : 'Server unreachable — playing offline (retrying…)';
    this.refresh();
  }

  refresh() {
    if (!document.body.contains(this.root)) return;
    const names = [...this.net.online.values()];
    this.listEl.innerHTML = names.length
      ? names.map((n) => `<li>${n.replace(/[<>&]/g, '')}</li>`).join('')
      : '<div class="empty">Nobody else right now — you would be first in</div>';
  }
}
